import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { StatCard } from "@/components/stat-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { PredictionsPanel } from "@/features/predictions";
import { PredictivePanel } from "@/features/predictive";
import { PremortemPanel } from "@/features/premortem";
import { apiPost } from "@/lib/api-client";
import { asRecord } from "@/lib/type-utils";

interface PredictionFormState {
  question: string;
  marketPriceCents: string;
  depth: "quick" | "standard" | "deep";
  marketId: string;
}

interface ResultEnvelope {
  endpoint: string;
  payload: unknown;
}

function numericField(source: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number") return value;
  }
  return null;
}

export function PredictionEnginePage() {
  const [form, setForm] = useState<PredictionFormState>({
    question: "",
    marketPriceCents: "50",
    depth: "standard",
    marketId: "",
  });
  const [lastResult, setLastResult] = useState<ResultEnvelope | null>(null);
  const [lastFailure, setLastFailure] = useState<string | null>(null);

  const marketPrice = useMemo(() => {
    const parsed = Number(form.marketPriceCents) / 100;
    if (!Number.isFinite(parsed)) return 0.5;
    return Math.max(0.001, Math.min(0.999, parsed));
  }, [form.marketPriceCents]);

  const normalizedQuestion = useMemo(() => form.question.trim(), [form.question]);
  // Backend validates question/market_question with min_length=10
  // (backend/routers/prediction_engine.py). No placeholder fallback — a blank
  // question must never silently fire a paid default analysis.
  const questionReady = normalizedQuestion.length >= 10;

  const handleFailure = (error: unknown) => {
    setLastFailure(error instanceof Error ? error.message : "Request failed");
  };

  const commonPayload = useMemo(() => {
    return {
      question: normalizedQuestion,
      market_price: marketPrice,
      depth: form.depth,
      market_id: form.marketId || undefined,
    };
  }, [form.depth, form.marketId, marketPrice, normalizedQuestion]);

  const runAnalysisMutation = useMutation({
    mutationFn: (endpoint: string) => {
      if (endpoint === "/api/predict/data") {
        return apiPost<unknown>(endpoint, {
          query: normalizedQuestion,
          limit_per_source: 10,
        });
      }

      if (endpoint === "/api/predict/red-team") {
        return apiPost<unknown>(endpoint, {
          question: normalizedQuestion,
          thesis: `Base thesis: ${normalizedQuestion}`,
          probability: marketPrice,
          confidence: 0.7,
          depth: form.depth,
        });
      }

      return apiPost<unknown>(endpoint, commonPayload);
    },
    onMutate: () => setLastFailure(null),
    onSuccess: (payload, endpoint) => setLastResult({ endpoint, payload }),
    onError: handleFailure,
  });

  const trackCalibrationMutation = useMutation({
    mutationFn: () =>
      apiPost<unknown>("/api/predict/calibration/track", {
        market_id: form.marketId || `manual-${Date.now()}`,
        market_question: normalizedQuestion,
        predicted_probability: marketPrice,
        confidence: 0.7,
        market_price: marketPrice,
        analysis_method: "manual-ui",
        category: "general",
      }),
    onMutate: () => setLastFailure(null),
    onSuccess: (payload) => {
      setLastResult({ endpoint: "/api/predict/calibration/track", payload });
    },
    onError: handleFailure,
  });

  const reportMutation = useMutation({
    mutationFn: () =>
      apiPost<unknown>("/api/predict/calibration/report", {
        model_name: "all",
        time_period: "all",
        num_bins: 10,
      }),
    onMutate: () => setLastFailure(null),
    onSuccess: (payload) => {
      setLastResult({ endpoint: "/api/predict/calibration/report", payload });
    },
    onError: handleFailure,
  });

  const resultRecord = asRecord(lastResult?.payload);
  const finalProbability = numericField(resultRecord, [
    "final_probability",
    "probability",
    "consensus_probability",
  ]);
  const edge = numericField(resultRecord, ["edge", "expected_edge"]);
  const confidence =
    (typeof resultRecord.final_confidence === "string" && resultRecord.final_confidence) ||
    (typeof resultRecord.confidence === "string" && resultRecord.confidence) ||
    "n/a";

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Side column sits beside the main form: PredictivePanel (per-market
          scanner analysis) and PremortemPanel (next to the Red Team action —
          both are adversarial paid checks). */}
      <div className="grid gap-4 md:gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Panel
          title="Prediction Engine"
          description="Run full AI prediction pipeline, research slices, red-team checks, and calibration tracking."
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>Market Question</Label>
              <Textarea
                value={form.question}
                onChange={(event) => setForm((prev) => ({ ...prev, question: event.target.value }))}
                rows={3}
                placeholder="Will the Fed cut rates by June?"
              />
            </div>

            <div>
              <Label>Market Price (cents)</Label>
              <Input
                value={form.marketPriceCents}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, marketPriceCents: event.target.value }))
                }
              />
            </div>

            <div>
              <Label>Depth</Label>
              <Select
                value={form.depth}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    depth: event.target.value === "quick" || event.target.value === "deep"
                      ? event.target.value
                      : "standard",
                  }))
                }
              >
                <option value="quick">Quick</option>
                <option value="standard">Standard</option>
                <option value="deep">Deep</option>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label>Optional Market ID</Label>
              <Input
                value={form.marketId}
                onChange={(event) => setForm((prev) => ({ ...prev, marketId: event.target.value }))}
                placeholder="Polymarket market_id for calibration linkage"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              onClick={() => runAnalysisMutation.mutate("/api/predict/full-analysis")}
              disabled={runAnalysisMutation.isPending || !questionReady}
            >
              Full Analysis
            </Button>
            <Button
              variant="ghost"
              onClick={() => runAnalysisMutation.mutate("/api/predict/research")}
              disabled={runAnalysisMutation.isPending || !questionReady}
            >
              Research
            </Button>
            <Button
              variant="ghost"
              onClick={() => runAnalysisMutation.mutate("/api/predict/red-team")}
              disabled={runAnalysisMutation.isPending || !questionReady}
            >
              Red Team
            </Button>
            <Button
              variant="ghost"
              onClick={() => runAnalysisMutation.mutate("/api/predict/data")}
              disabled={runAnalysisMutation.isPending || !questionReady}
            >
              Data Pipeline
            </Button>
            <Button
              variant="ghost"
              onClick={() => trackCalibrationMutation.mutate()}
              disabled={trackCalibrationMutation.isPending || !questionReady}
            >
              Track Calibration
            </Button>
            <Button
              variant="ghost"
              onClick={() => reportMutation.mutate()}
              disabled={reportMutation.isPending}
            >
              Calibration Report
            </Button>
          </div>
          {!questionReady ? (
            <p className="mt-2 text-xs text-amber-400/80">
              Enter a market question (at least 10 characters) to run analyses. Calibration Report needs no question.
            </p>
          ) : null}
        </Panel>

        <div className="space-y-4 md:space-y-6">
          <PredictivePanel />
          <PremortemPanel />
        </div>
      </div>

      {lastFailure ? (
        <div
          role="alert"
          className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300"
        >
          <span className="font-medium">Last request failed:</span> {lastFailure}
          {lastFailure.toLowerCase().includes("timed out")
            ? " The analysis may still be running (and spending) server-side."
            : ""}
          {lastResult ? " Results below show the previous successful run." : ""}
        </div>
      ) : null}

      <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
        <StatCard
          label="Final Probability"
          value={finalProbability !== null ? `${(finalProbability * 100).toFixed(1)}%` : "n/a"}
          hint="Derived from response payload"
        />
        <StatCard
          label="Edge"
          value={edge !== null ? `${(edge * 100).toFixed(2)}%` : "n/a"}
          hint="Probability - market price"
        />
        <StatCard label="Confidence" value={confidence} hint="Model-reported confidence tier" />
        <StatCard
          label="Last Endpoint"
          value={lastResult?.endpoint ?? "n/a"}
          hint="Most recent analysis action"
        />
      </div>

      <Panel
        title="Latest Prediction Payload"
        description={
          lastFailure && lastResult
            ? "Stale — showing the previous successful run; the latest request failed."
            : "Raw response from latest prediction request."
        }
      >
        <JsonView value={lastResult?.payload} />
      </Panel>

      <PredictionsPanel />
    </div>
  );
}
